import * as React from 'react'
import { Loader2 } from 'lucide-react'
import { mcpOrgSettingSchema, type McpOrgSetting } from '@lib/enums/mcp-org-setting.enum'
import { ErrorState, LoadingState } from '@/components/PageState'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Field } from '@/components/ui/field'
import { Select } from '@/components/ui/select'
import { toast } from '@/components/ui/toast'
import { apiErrorMessage } from '@/helpers/apiErrorMessage'
import { useConnectedAppsActions, useMcpSetting } from '@/hooks/useConnectedApps'

const SETTING_OPTIONS: Record<McpOrgSetting, string> = {
  off: 'Off: AI apps can’t reach this organization',
  test_only: 'Test only (default)',
  live_read: 'Live read-only: AI apps can read live data',
  full: 'Full: live changes for connections granted them',
}

const SHORT_LABELS: Record<McpOrgSetting, string> = { off: 'Off', test_only: 'Test only', live_read: 'Live read-only', full: 'Full' }

/**
 * The organization's cap on AI apps over MCP. Owners change it; lowering it
 * applies to every connection at once, and raising it doesn't give back
 * access people already lost (they connect again for more).
 */
export function McpSettingCard({ canEdit }: { canEdit: boolean }) {
  const query = useMcpSetting({ enabled: true })
  const { updateSetting } = useConnectedAppsActions()
  const [draft, setDraft] = React.useState<McpOrgSetting | null>(null)
  const [error, setError] = React.useState<string | null>(null)

  if (query.isLoading) return <LoadingState />
  if (query.isError || !query.data) return <ErrorState message={apiErrorMessage(query.error, 'Could not load the MCP setting.')} />
  const current = query.data.setting
  const value = draft ?? current

  async function save(): Promise<void> {
    setError(null)
    try {
      await updateSetting.mutateAsync(value)
      setDraft(null)
      toast.success('MCP setting saved')
    } catch (failure) {
      setError(apiErrorMessage(failure, 'Could not save the MCP setting.'))
    }
  }

  return (
    <Card>
      <CardContent className="space-y-3 pt-card">
        {canEdit ? (
          <>
            <Field label="Allow" htmlFor="mcp-setting" hint="Lowering it applies to every connected app at once. Raising it doesn’t give back access people already lost; they connect again for more." {...(error ? { error } : {})}>
              <Select
                id="mcp-setting"
                value={value}
                onChange={(event) => {
                  const parsed = mcpOrgSettingSchema.safeParse(event.target.value)
                  if (parsed.success) setDraft(parsed.data)
                }}
              >
                {mcpOrgSettingSchema.options.map((option) => (
                  <option key={option} value={option}>
                    {SETTING_OPTIONS[option]}
                  </option>
                ))}
              </Select>
            </Field>
            <div className="flex justify-end">
              <Button disabled={value === current || updateSetting.isPending} onClick={() => void save()}>
                {updateSetting.isPending ? <Loader2 className="animate-spin" /> : null}
                Save
              </Button>
            </div>
          </>
        ) : (
          <div className="flex items-center gap-2 text-body">
            <span className="text-muted-foreground">Allowed:</span>
            <Badge>{SHORT_LABELS[current]}</Badge>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
